import { Component, Input, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import * as Highcharts from "highcharts";
import * as moment from "moment";
import { BatteryType, EventService } from "src/app/services/event.service";
import Swal from "sweetalert2";
import { DataByBatteryType } from "../histogram.component";

interface Signal {
  signal: string;
  data_type: string;
  label: string;
}

interface HistogramConfig {
  binWidth: number | undefined;
  startRange: number | undefined;
  endRange: number | undefined;
}

interface HistogramData {
  value: number;
  pack_id: string;
}

@Component({
  selector: "app-histogram-template",
  templateUrl: "./histogram-template.component.html",
  styleUrls: ["./histogram-template.component.css"],
})
export class HistogramTemplateComponent implements OnInit {
  @Input() dataByBatteryType: DataByBatteryType;

  constructor(private eventService: EventService) {}
  isSignalsListFetched: boolean = false;
  isHistogramDataLoading: boolean = false;
  isHistogramDataReceived: boolean = false;

  signalsList: Signal[] = [];
  productsList: string[] = [];
  variantList: string[] = [];
  rigsList: string[] = [];
  batteryType: BatteryType = null;

  histogramData: HistogramData[] = [];
  histogramSignal: Signal = null;
  histogramConfig: HistogramConfig = {
    binWidth: undefined,
    startRange: undefined,
    endRange: undefined,
  };
  masterSignalsList: Signal[] = [];
  masterVariantsList: Record<string, string[]> = {};

  histogramForm: FormGroup = new FormGroup({
    selectedSignal: new FormControl({}),
    selectedProduct: new FormControl("*"),
    selectedVariant: new FormControl("*"),
    selectedRig: new FormControl("*"),
    startDate: new FormControl(null),
    endDate: new FormControl(null),
    signalSearchKeyword: new FormControl(""),
  });

  get selectedSignal() {
    return this.histogramForm.controls["selectedSignal"].value as Signal;
  }

  ngOnInit(): void {
    const { batteryType, productsList, rigsList, signalsList, variantList } =
      this.dataByBatteryType;

    this.masterSignalsList = signalsList;
    this.masterVariantsList = variantList;
    this.productsList = productsList;
    this.rigsList = rigsList;
    this.signalsList = signalsList;
    this.batteryType = batteryType;

    this.isSignalsListFetched = true;
  }

  updateSignalList(event: KeyboardEvent) {
    event.preventDefault();

    const keyword = (this.histogramForm.value.signalSearchKeyword as string)
      .trim()
      .toLowerCase();

    this.signalsList = this.masterSignalsList.filter((signalData) =>
      signalData.label.toLowerCase().includes(keyword)
    );
  }

  setVariantList() {
    const selectedProduct = this.histogramForm.value.selectedProduct as string;
    this.variantList = this.masterVariantsList[selectedProduct];
    this.histogramForm.controls.selectedVariant.setValue("*");
  }

  setHistogramDataStatus(status: "fetched" | "fetching") {
    if (status === "fetched") {
      this.isHistogramDataReceived = true;
      this.isHistogramDataLoading = false;
    } else {
      this.isHistogramDataReceived = false;
      this.isHistogramDataLoading = true;
    }
  }

  resetHistogramFilters() {
    this.histogramConfig = {
      binWidth: undefined,
      startRange: undefined,
      endRange: undefined,
    };
  }

  updateChartFilter() {
    const startRange = this.histogramConfig.startRange;
    const endRange = this.histogramConfig.endRange;
    const binWidth = this.histogramConfig.binWidth;

    if (binWidth < 0) {
      Swal.fire({
        icon: "error",
        title: "Invalid Bin Width",
        text: "Bin width cannot be negative",
      });
      return;
    }

    const startRangeDefined = startRange !== undefined && startRange !== null;
    const endRangeDefined = endRange !== undefined && endRange !== null;
    const bothRangesDefined = startRangeDefined && endRangeDefined;

    if (bothRangesDefined && startRange > endRange) {
      Swal.fire({
        icon: "error",
        title: "Invalid Range",
        text: "Start range cannot be greater than end range",
      });
      return;
    }

    const filterData = this.histogramData.filter((data) => {
      const { value } = data;
      if (bothRangesDefined) {
        return value >= startRange && value <= endRange;
      } else if (startRangeDefined && !endRangeDefined) {
        return value >= startRange;
      } else if (!startRangeDefined && endRangeDefined) {
        return value <= endRange;
      } else {
        return true;
      }
    });

    this.setHistogramDataStatus("fetching");
    if (binWidth) {
      const binNumber = Math.floor((endRange - startRange) / binWidth);
      this.plotChart(filterData, { binWidth, binNumber });
    } else {
      this.plotChart(filterData);
    }
    this.setHistogramDataStatus("fetched");
  }

  getHistogramData() {
    const {
      selectedSignal,
      selectedProduct,
      selectedVariant,
      selectedRig,
      startDate,
      endDate,
    } = this.histogramForm.value;

    if (startDate && !endDate) {
      Swal.fire({
        icon: "error",
        title: "No End Date Selected",
        text: "Please select an end date to continue",
      });
      return;
    }

    // Signal

    if (!Object.keys(selectedSignal).length) {
      Swal.fire({
        icon: "error",
        title: "No Signal Selected",
        text: "Please select a signal to continue",
      });
      return;
    }

    this.setHistogramDataStatus("fetching");
    this.resetHistogramFilters();

    let payload = {};
    if (selectedProduct !== "*") {
      payload["product"] = selectedProduct;
      if (selectedVariant !== "*") {
        payload["variant"] = selectedVariant;
      }
    }
    if (selectedRig !== "*") {
      payload["rig"] = selectedRig;
    }

    if (startDate && endDate) {
      payload["startDate"] = moment(startDate).format("YYYY-MM-DD");
      payload["endDate"] = moment(endDate).format("YYYY-MM-DD");
    }

    this.eventService
      .getHistogramDataBySignal({
        signal: selectedSignal.signal,
        batteryType: this.batteryType,
        ...payload,
      })
      .subscribe((res) => {
        if (res.success) {
          this.setHistogramDataStatus("fetched");
          this.histogramData = res.data;
          this.histogramSignal = selectedSignal;
          setTimeout(() => {
            this.plotChart(this.histogramData);
          }, 0);
        }
      });
  }

  plotChart(
    histogramData: HistogramData[],
    configs?: {
      binWidth: number;
      binNumber: number;
    }
  ) {
    const data: any = histogramData.map((_) => _.value);

    const calculateWithPrecision = (value) => {
      const decimalPlaces = 10;
      const multiplier = Math.pow(10, decimalPlaces);
      return Math.round(value * multiplier) / multiplier;
    };

    const getValidData = ({ data, startRange, endRange, binWidth }) => {
      let validData = data;

      if (startRange && endRange) {
        validData = data.filter((_) => _ >= startRange && _ <= endRange);
      } else if (startRange && !endRange) {
        validData = data.filter((_) => _ >= startRange);
      } else if (!startRange && endRange) {
        validData = data.filter((_) => _ <= endRange);
      }

      const maxVal = endRange || Math.max(...validData);
      const minVal = startRange || Math.min(...validData);
      const bw = binWidth || calculateWithPrecision((maxVal - minVal) / 10);

      return {
        data: validData,
        maxVal,
        minVal,
        binWidth: bw,
      };
    };

    const {
      binWidth: binWidthInput,
      endRange: endRangeInput,
      startRange: startRangeInput,
    } = this.histogramConfig;

    const {
      data: plottingData,
      binWidth,
      maxVal: endRange,
      minVal: startRange,
    } = getValidData({
      data,
      startRange: startRangeInput,
      endRange: endRangeInput,
      binWidth: binWidthInput,
    });

    if (this.histogramSignal.data_type === "string") {
      const stringOption: Highcharts.Options = {
        title: { text: this.histogramSignal.label },
        xAxis: { labels: { enabled: false } },
        yAxis: [{ title: { text: "Frequency" } }],
        legend: { enabled: false },
        exporting: { enabled: false },
        credits: { enabled: false },
        tooltip: {
          formatter: function () {
            return `<b>${this.x === 0 ? "Fail: " : "Pass: "}</b> ${this.y}`;
          },
        },
        plotOptions: {
          histogram: {
            binsNumber: 2,
            zoneAxis: "x",
            color: data[0] === 1 ? "#09865d" : "#cf2c2c",
            zones: [
              { value: 0.5, color: "#cf2c2c" },
              { value: 1, color: "#09865d" },
            ],
          },
        },
        series: [
          {
            name: this.histogramSignal.label,
            type: "histogram",
            xAxis: 0,
            yAxis: 0,
            baseSeries: "s1",
            cursor: "pointer",
            events: {
              click: (e) => {
                const { category } = e.point;

                const name = Number(category) === 0 ? "Fail" : "Pass";

                let packIds = Array.from(
                  new Set(
                    histogramData
                      .filter((_) =>
                        name === "Fail" ? _.value === 0 : _.value === 1
                      )
                      .map((_) => _.pack_id)
                  )
                );

                this.firePackIdsSwal(packIds, name);
              },
            },
          },
          {
            name: "Data",
            type: "scatter",
            data: plottingData,
            id: "s1",
            marker: {
              radius: 0,
            },
            visible: false,
            showInLegend: false,
          },
        ],
      };

      Highcharts.chart("histogram", stringOption);
    } else {
      const getVaildBinIndex = (
        value: number,
        binSet: { [key: string]: number }
      ) => {
        const bins = Object.keys(binSet).map((key) =>
          key.split(" - ").map(Number)
        );
        let binIndex = -1;

        bins.forEach(([start, end], index) => {
          if (value >= start && value < end) {
            binIndex = index;
          }
        });

        return binIndex;
      };

      const calculateHistogramData = (data, startRange, endRange, binWidth) => {
        const bins = [];
        const numBins = Math.ceil((endRange - startRange) / binWidth);
        let binIndexSet = {};

        // Initialize bins
        for (let i = 0; i < numBins; i++) {
          const binStart = calculateWithPrecision(startRange + i * binWidth);
          const binEnd = calculateWithPrecision(
            startRange + (i + 1) * binWidth
          );
          const binData = {
            x: binStart,
            y: 0,
            name: `${binStart} - ${binEnd}`,
          };
          bins.push(binData);
          binIndexSet[binData.name] = i;
        }

        // Count data points in each bin
        data.forEach((value) => {
          const binIndex = getVaildBinIndex(value, binIndexSet);

          if (binIndex >= 0 && binIndex < bins.length) {
            bins[binIndex].y++;
          }
        });

        return bins;
      };

      const histogramBins = calculateHistogramData(
        plottingData,
        startRange,
        endRange,
        binWidth
      );

      const numberOption: Highcharts.Options = {
        title: { text: this.histogramSignal.label },
        xAxis: [
          {
            title: { text: "" },
            min: startRange,
            max: endRange,
            tickInterval: binWidth,
            startOnTick: false,
            endOnTick: false,
            tickPositions: Array.from(
              { length: Math.ceil((endRange - startRange) / binWidth) + 1 },
              (_, i) => calculateWithPrecision(startRange + i * binWidth)
            ),
            labels: {
              formatter: function () {
                return calculateWithPrecision(this.value) as any;
              },
            },
          },
        ],
        yAxis: [{ title: { text: "" } }],
        legend: { enabled: false },
        exporting: { enabled: false },
        credits: { enabled: false },
        plotOptions: {
          column: {
            pointPadding: 0,
            groupPadding: 0,
            borderWidth: 1,
            pointPlacement: "between",
          },
        },
        series: [
          {
            name: "Frequency",
            type: "column",
            data: histogramBins,
            pointStart: startRange,
            pointInterval: binWidth,
            events: {
              click: (e) => {
                const { name: dataRange } = e.point.options as any;
                const [lowerRange, higherRange] = (dataRange as string)
                  .split(" - ")
                  .map(Number);

                const packIds = Array.from(
                  new Set(
                    this.histogramData
                      .filter(
                        (_) =>
                          plottingData.includes(_.value) &&
                          _.value >= lowerRange &&
                          _.value < higherRange
                      )
                      .map((_) => {
                        return _.pack_id;
                      })
                  )
                );

                this.firePackIdsSwal(packIds, dataRange);
              },
            },
          },
        ],
      };

      Highcharts.chart("histogram", numberOption);
    }
  }

  private firePackIdsSwal(packIds: string[], dataRange?: string) {
    const div = document.createElement("div");
    const ol = document.createElement("ol");

    packIds.forEach((pack) => {
      const li = document.createElement("li");
      li.textContent = pack;
      ol.appendChild(li);

      li.style.marginTop = "0.2rem";
      li.style.textAlign = "left";
      li.style.listStyleType = "none";
    });
    div.appendChild(ol);

    ol.style.display = "grid";
    ol.style.gridTemplateColumns = "repeat(3, 1fr)";
    ol.style.gap = "1rem";

    div.style.maxHeight = "400px";
    div.style.overflowY = "scroll";
    div.style.marginTop = "1rem";

    Swal.fire({
      title: `<h4>${dataRange}<span style="margin-left: 1rem; opacity: 0.8">(${packIds.length})</span></h4>`,
      html: div,
      width: 800,
      showConfirmButton: false,
      showCloseButton: true,
    });
  }
}
